import { useMemo } from 'react'

import toFixedValue from 'app/common/lib/toFixedValue'
import { SummaryItem, SummaryWrap, SummaryRow } from 'app/components/common/reports/summary'

const convertToString = (val: number | void, currency: string) => {
  return typeof val === 'number' ? `${toFixedValue(val)} ${currency}` : '—'
}

const groups = [/^(Всего)/i, /^(Собст|Комме|Выпеч|Горяч)/i, /^(Внутр|СБП|Банко|Налич)/i]

const groupOrderParts = groups.map((group) => {
  const match = group.source.match(/^\^\((.+)\)$/)
  const parts = (match ? match[1] : group.source.replace(/^\^/, '')).split('|')
  return parts
})

const getGroupOrderIndex = (title: string, groupIndex: number) => {
  const order = groupOrderParts[groupIndex]
  if (!order) return Number.POSITIVE_INFINITY
  const index = order.findIndex((part) => title.startsWith(part))
  return index === -1 ? Number.POSITIVE_INFINITY : index
}

const convertLabelSummary = {
  SBP: 'СБП',
}

type Summary = Array<{ title: string; sum: number; n: number }>

export const SummaryByPaymentMethod = ({
  summary,
  currency,
  showCount,
  itemClassName,
}: {
  summary: Summary
  currency: string
  showCount?: boolean
  itemClassName?: string
}) => {
  const rows = useMemo(() => {
    const allItems = summary.map(
      ({ title, sum, n }) =>
        [
          convertLabelSummary[title] || title,
          showCount
            ? [convertToString(sum, currency), String(n)]
            : [convertToString(sum, currency)],
        ] as [string, Array<string>],
    )

    const groupedRows = allItems.reduce(
      (acc, item) => {
        const [title] = item
        const index = groups.findIndex((regex) => regex.test(title))
        if (index !== -1) acc[index].push(item)
        return acc
      },
      groups.map(() => []) as Array<Array<[string, Array<string>]>>,
    )
    return groupedRows
      .map((row, groupIndex) => {
        if (row.length <= 1) return row
        const withIndex = row.map((item, originalIndex) => ({
          item,
          originalIndex,
          orderIndex: getGroupOrderIndex(item[0], groupIndex),
        }))
        withIndex.sort((a, b) => {
          if (a.orderIndex !== b.orderIndex) return a.orderIndex - b.orderIndex
          return a.originalIndex - b.originalIndex
        })
        return withIndex.map(({ item }) => item)
      })
      .filter((row) => row?.length !== 0)
  }, [summary, currency, showCount])

  return (
    <SummaryWrap>
      {rows.map((row, i) => (
        <SummaryRow key={i}>
          {row.map(([title, value]) => (
            <SummaryItem key={title} className={itemClassName} title={title} value={value} />
          ))}
        </SummaryRow>
      ))}
    </SummaryWrap>
  )
}
